/**
 * TOSS Web Application — Checkbox
 *
 * **Коробочка нарисована, а не отдана браузеру (03.09.2026).** До этого дня здесь стоял голый
 * `<input type="checkbox">` с классами `rounded text-accent-500`: обе утилиты работают только с
 * плагином `@tailwindcss/forms`, а он в проекте не подключён (`tailwind.config.js`, `plugins: []`).
 * Поэтому квадратик рисовала операционная система — своим синим, своим радиусом и своей галочкой,
 * и в панели фильтров прайс-листа владелец увидел ровно это: «цвета не те, скругления у чекбокса
 * не такие».
 *
 * Теперь `appearance-none` и вид от нашей палитры: рамка `--color-border` на фоне карточки, в
 * отмеченном состоянии — заливка `--color-primary-500` и галочка `--color-text-on-primary`.
 * Радиус `--radius-sm`, как у коробочки выбора в справочном пикере (`EntityPickerRow`), — это
 * один и тот же элемент в двух местах продукта, и выглядеть он должен одинаково.
 *
 * Элемент остался НАСТОЯЩИМ `<input>`: на нём `ref`, `name` и `register()` из react-hook-form,
 * он же носитель состояния для скринридера. Галочка лежит поверх (`peer-checked`) и не ловит
 * события.
 */

import {
  forwardRef,
  type InputHTMLAttributes,
  useId,
} from 'react';
import { Check } from 'lucide-react';

// ============================================================================
// Checkbox Component
// ============================================================================

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Label for the checkbox */
  label?: string;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
}

/**
 * Checkbox component
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label="Accept terms and conditions"
 *   {...register('acceptTerms')}
 * />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error,
      helperText,
      className = '',
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const checkboxId = providedId ?? generatedId;
    const hasError = !!error;

    return (
      <div>
        <div className="flex items-start gap-2">
          {/* 18 px явными пикселями, как у коробочки в `EntityPickerRow`: числовые классы Tailwind
              считаются от корневого кегля, а он в проекте плавающий, и `h-4` давало 17.6 px. */}
          <span className="relative mt-0.5 inline-flex h-[18px] w-[18px] flex-shrink-0">
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              className={`
                peer h-[18px] w-[18px] appearance-none rounded-[var(--radius-sm)] border
                border-[var(--color-border)] bg-[var(--color-bg-card)] cursor-pointer
                transition-colors
                checked:border-[var(--color-primary-500)] checked:bg-[var(--color-primary-500)]
                focus:outline-none focus-visible:ring-2
                focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_35%,transparent)]
                disabled:cursor-not-allowed disabled:bg-[var(--color-bg-secondary)]
                disabled:checked:bg-[var(--color-text-disabled)]
                disabled:checked:border-[var(--color-text-disabled)]
                ${hasError ? 'border-[var(--color-error)]' : ''}
                ${className}
              `}
              aria-invalid={hasError}
              {...props}
            />
            <Check
              className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-[var(--color-text-on-primary)] opacity-0 peer-checked:opacity-100"
              strokeWidth={3}
              aria-hidden="true"
            />
          </span>
          {label && (
            <label
              htmlFor={checkboxId}
              className="text-sm text-[var(--color-text-primary)] cursor-pointer select-none"
            >
              {label}
            </label>
          )}
        </div>

        {hasError && (
          <p className="mt-1 text-sm text-[var(--color-error)]">{error}</p>
        )}

        {helperText && !hasError && (
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{helperText}</p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
